import { useState } from 'react';

import Accordion from 'components/Accordion';
import Button from 'components/Button';
import {
	LoadingSkeleton,
	LoadingSkeletonItem,
} from 'components/LoadingSkeleton';
import LoadMoreList from 'components/LoadMoreList';
import { PartialPopover } from 'components/Popover';
import type { FieldKey } from 'hooks';
import type { VariantId } from 'models/product';
import type { TemplateString } from 'types';
import { formatTemplate } from 'utils/format';
import { is, range } from 'utils/helpers';
import { useI18n } from 'utils/i18n';

import { useProductQuestionsContext } from '../ProductDetailsContext';

import ProductQuestion from './ProductQuestion';

interface Props {
	askQuestionButtonText: string;
	feedbackDoneText: string;
	introText: FieldKey | TemplateString;
	loadMoreButtonText: string;
	popoverHeading: string;
	productName: string;
	reportButtonText: string;
	title: string;
	upvoteButtonText: string;
	variantId: VariantId;
}

export default function ProductQuestions({
	askQuestionButtonText,
	feedbackDoneText,
	introText,
	loadMoreButtonText,
	popoverHeading,
	productName,
	reportButtonText,
	title,
	upvoteButtonText,
	variantId,
}: Props) {
	const { tPlural } = useI18n();
	const [isFormOpen, setIsFormOpen] = useState(false);
	const {
		hasNextPage,
		isLoading,
		isLoadingMore,
		loadMore,
		newQuestionsAllowed,
		questions,
	} = useProductQuestionsContext();

	if (!is.arrayWithLength(questions) && !newQuestionsAllowed) {
		return null;
	}

	return (
		<Accordion title={title} id="product-questions">
			<p>
				{formatTemplate(introText, {
					productName,
				})}
			</p>
			{newQuestionsAllowed && (
				<Button
					className="mt-8"
					onClick={() => setIsFormOpen((current) => !current)}
					aria-haspopup="dialog"
					variant="branded"
				>
					{askQuestionButtonText}
				</Button>
			)}

			{isLoading && (
				<div className="mt-8 divide-y divide-strong">
					{range(2).map((index) => (
						<LoadingSkeleton key={index} className="py-8">
							{/* Name and date */}
							<LoadingSkeletonItem height="1rem" width="5rem" />
							<LoadingSkeletonItem
								height="1rem"
								width="13rem"
								className="mt-1"
							/>
							{/* Question */}
							<LoadingSkeletonItem
								height="1.5rem"
								width="90%"
								className="my-4"
							/>
							{/* Answer box */}
							<LoadingSkeletonItem
								height="5rem"
								width="100%"
								className="mt-6"
							/>
							{/* Like/report buttons */}
							<LoadingSkeletonItem
								height="2rem"
								width="12rem"
								className="mt-6"
							/>
						</LoadingSkeleton>
					))}
				</div>
			)}
			{!isLoading && (
				<LoadMoreList
					loadedItemFocusTarget="container"
					onLoadMoreClick={loadMore}
					isLoading={isLoadingMore}
					buttonVariant="secondary"
					buttonText={loadMoreButtonText}
					itemCountScreenReaderText={tPlural(
						'product_questions_item_count_text',
						questions?.length ?? 0,
					)}
					className="my-4"
					listClassName="divide-y divide-strong"
					buttonClassName="mx-auto mt-10 mb-6 flex min-w-[50%]"
					hasLoadMoreButton={hasNextPage}
				>
					{questions?.map((question) => (
						<ProductQuestion
							key={`${question.author}-${question.date}`}
							feedbackDoneText={feedbackDoneText}
							question={question}
							reportButtonText={reportButtonText}
							upvoteButtonText={upvoteButtonText}
						/>
					))}
				</LoadMoreList>
			)}
			<PartialPopover
				heading={popoverHeading}
				isOpen={isFormOpen}
				partial="questionsAndAnswers"
				onClose={() => setIsFormOpen(false)}
				partialQueryVars={{
					'token-variantId': variantId,
				}}
			/>
		</Accordion>
	);
}
ProductQuestions.displayName = 'ProductQuestions';
